/**
 * @file PatientLogin — /login/paciente
 *
 * Passwordless login via Google OAuth or email magic code.
 */
import React, { useState } from "react";
import { useNavigate, useSearchParams, Link as RouterLink } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { ArrowLeft, AlertCircle } from "lucide-react";
import { authService } from "../services/auth";
import { useAuth } from "../hooks/useAuth";
import OAuthButtons from "../components/OAuthButtons";
import { AUTH_STRINGS as S } from "../constants/strings";
import AuthShell from "../components/AuthShell";
import { Button, Input } from "../ui";

const emailSchema = Yup.object({
  email: Yup.string().email(S.INVALID_EMAIL).required(S.REQUIRED_FIELD),
});

const GOOGLE_REDIRECT_ERRORS = {
  google_denied: "Inicio de sesión con Google cancelado.",
  google_invalid: "Error en la respuesta de Google. Intenta de nuevo.",
  google_csrf: "La sesión expiró. Intenta de nuevo.",
  google_exchange: "Error al comunicarse con Google. Intenta de nuevo.",
  google_no_token: "No se recibió respuesta de Google. Intenta de nuevo.",
  google_failed: "Error al iniciar sesión con Google. Intenta de nuevo.",
};

export default function PatientLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { handleAuthSuccess } = useAuth();
  const googleError = GOOGLE_REDIRECT_ERRORS[searchParams.get("error")];
  const [error, setError] = useState(googleError || "");

  const handleEmailSubmit = async (values, { setSubmitting }) => {
    try {
      setError("");
      await authService.requestCode(values.email);
      navigate("/verify-code", { state: { email: values.email } });
    } catch (err) {
      setError(err.response?.data?.message || "Error al enviar el código");
    } finally {
      setSubmitting(false);
    }
  };

  const handleNewGoogleUser = async (data) => {
    try {
      const res = await authService.googleRegister(data.credential, { role: "PACIENTE" });
      handleAuthSuccess(res.data);
      navigate("/");
    } catch (err) {
      setError(err.response?.data?.message || "Error al crear la cuenta");
    }
  };

  return (
    <AuthShell
      title="Ingreso paciente"
      subtitle="Accede sin contraseña con Google o tu correo"
      footer={
        <RouterLink to="/login" className="auth-back">
          <ArrowLeft size={12} /> Volver
        </RouterLink>
      }
    >
      <OAuthButtons
        role="PACIENTE"
        onSuccess={(data) => {
          handleAuthSuccess(data);
          navigate("/");
        }}
        onError={(msg) => setError(msg)}
        onNewGoogleUser={handleNewGoogleUser}
      />

      <div className="auth-divider">o usa código por correo</div>

      {error && (
        <div className="auth-alert auth-alert-error">
          <AlertCircle size={14} />
          <span>{error}</span>
        </div>
      )}

      <Formik initialValues={{ email: "" }} validationSchema={emailSchema} onSubmit={handleEmailSubmit}>
        {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
          <Form noValidate className="auth-form">
            <Input
              name="email"
              type="email"
              label={S.LOGIN_EMAIL}
              autoComplete="email"
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.email && errors.email}
            />
            <Button type="submit" size="lg" fullWidth loading={isSubmitting}>
              Enviar código por correo
            </Button>
          </Form>
        )}
      </Formik>

      <div className="auth-links">
        <span className="auth-link-row">
          ¿No tienes cuenta?{" "}
          <RouterLink to="/register/patient" className="auth-link">
            Regístrate
          </RouterLink>
        </span>
      </div>
    </AuthShell>
  );
}
